import React, { useState, useEffect } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { Heart, Shield, Activity, Lock, User } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import config from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolWebClientId
});

const Auth = ({ onAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaSetupSecret, setMfaSetupSecret] = useState('');
  const [cognitoUser, setCognitoUser] = useState(null);

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err, session) => {
        if (err) {
          console.error('Session error:', err);
          return;
        }
        if (session.isValid()) {
          onAuthenticated(session.getIdToken().getJwtToken());
        }
      });
    }
  }, [onAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password
    });

    const user = new CognitoUser({
      Username: username,
      Pool: userPool
    });

    user.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        setLoading(false);
        onAuthenticated(session.getIdToken().getJwtToken());
      },
      onFailure: (err) => {
        setLoading(false);
        setError(err.message || 'Login failed');
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        setLoading(false);
        setCognitoUser(user);
        setNeedsPasswordReset(true);
      },
      totpRequired: () => {
        setLoading(false);
        setCognitoUser(user);
        setMfaRequired(true);
      },
      mfaSetup: () => {
        // Pool requires MFA and this user isn't enrolled yet - enroll them
        // right here as part of sign-in.
        setCognitoUser(user);
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            setMfaSetupSecret(secretCode);
            setMfaSetupRequired(true);
            setLoading(false);
          },
          onFailure: (err) => {
            setLoading(false);
            setError(err.message || 'Could not start MFA setup');
          }
        });
      }
    });
  };

  const handleMfaSetupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // In the login-time setup flow, a successful verify completes
    // authentication and returns the session directly.
    cognitoUser.verifySoftwareToken(mfaCode.trim(), 'BlackCapHealth Console', {
      onSuccess: (session) => {
        setLoading(false);
        onAuthenticated(session.getIdToken().getJwtToken());
      },
      onFailure: (err) => {
        setLoading(false);
        setError(err.message || 'Code did not match - try again');
      }
    });
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    cognitoUser.sendMFACode(mfaCode.trim(), {
      onSuccess: (session) => {
        setLoading(false);
        onAuthenticated(session.getIdToken().getJwtToken());
      },
      onFailure: (err) => {
        setLoading(false);
        setError(err.message || 'Invalid code - try again');
      }
    }, 'SOFTWARE_TOKEN_MFA');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setError('');
    setLoading(true);

    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => {
        setLoading(false);
        onAuthenticated(session.getIdToken().getJwtToken());
      },
      onFailure: (err) => {
        setLoading(false);
        setError(err.message || 'Password reset failed');
      },
      // With MFA enforced, Cognito chains straight into the next challenge
      // after the password is set - handle both possibilities here too.
      mfaSetup: () => {
        setError('');
        setNeedsPasswordReset(false);
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            setMfaSetupSecret(secretCode);
            setMfaSetupRequired(true);
            setLoading(false);
          },
          onFailure: (err) => {
            setLoading(false);
            setError(err.message || 'Could not start MFA setup');
          }
        });
      },
      totpRequired: () => {
        setError('');
        setLoading(false);
        setNeedsPasswordReset(false);
        setMfaRequired(true);
      }
    });
  };

  if (mfaSetupRequired) {
    const otpauthUri = `otpauth://totp/BlackCapHealth:${encodeURIComponent(username)}?secret=${mfaSetupSecret}&issuer=BlackCapHealth`;
    return (
      <div className="min-h-screen bg-spruce-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-5">
            <Shield className="mx-auto text-spruce-600 mb-3" size={36} />
            <h1 className="text-xl font-bold text-ink">Set up two-factor authentication</h1>
            <p className="text-sm text-ink-soft mt-1">Your organization requires an authenticator app. Scan this code with Google Authenticator, Authy, or 1Password:</p>
          </div>
          <div className="flex justify-center mb-4">
            <div className="bg-white border border-line rounded-xl p-3">
              <QRCodeSVG value={otpauthUri} size={176} level="M" />
            </div>
          </div>
          <details className="mb-5 text-center">
            <summary className="text-xs text-spruce-600 cursor-pointer hover:text-spruce-800">Can't scan? Show setup key</summary>
            <code className="font-data text-[11px] break-all block mt-2 bg-mist border border-line rounded px-3 py-2 text-left">{mfaSetupSecret}</code>
          </details>
          <form onSubmit={handleMfaSetupSubmit} className="space-y-4">
            <input
              type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              className="w-full text-center text-2xl tracking-[0.5em] font-data px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
              placeholder="000000"
            />
            {error && <div className="text-danger text-sm text-center">{error}</div>}
            <button
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              className="w-full bg-spruce-600 text-white py-3 rounded-lg font-semibold hover:bg-spruce-700 disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify & continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (mfaRequired) {
    return (
      <div className="min-h-screen bg-spruce-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-6">
            <Shield className="mx-auto text-spruce-600 mb-3" size={36} />
            <h1 className="text-xl font-bold text-ink">Two-Factor Authentication</h1>
            <p className="text-sm text-ink-soft mt-1">Enter the 6-digit code from your authenticator app</p>
          </div>
          <form onSubmit={handleMfaSubmit} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              className="w-full text-center text-2xl tracking-[0.5em] font-data px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
              placeholder="000000"
              autoFocus
            />
            {error && <div className="text-danger text-sm text-center">{error}</div>}
            <button
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              className="w-full bg-spruce-600 text-white py-3 rounded-lg font-semibold hover:bg-spruce-700 disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (needsPasswordReset) {
    return (
      <div className="min-h-screen bg-spruce-900 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-spruce-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-spruce-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-700"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-spruce-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
          <div className="flex justify-center mb-6">
            <div className="bg-spruce-700 p-4 rounded-2xl">
              <Shield className="text-white" size={40} />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Set New Password</h2>
          <p className="text-gray-600 mb-8 text-center">Create a secure password for your account</p>
          
          <form onSubmit={handlePasswordReset} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-spruce-500 focus:border-transparent transition"
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-spruce-500 focus:border-transparent transition"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg flex items-start gap-2">
                <span className="text-red-500 mt-0.5">!</span>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-spruce-600 text-white py-3 px-6 rounded-xl hover:bg-spruce-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Setting Password...
                </span>
              ) : (
                'Set New Password'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spruce-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-spruce-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-spruce-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '700ms'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-spruce-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '1000ms'}}></div>
      </div>

      {/* Main login card */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative z-10">
        {/* Header section with gradient */}
        <div className="bg-spruce-700 p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-4 rounded-2xl">
              <Heart className="text-white" size={48} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            BlackCapHealth
          </h1>
          <p className="text-spruce-100">Admin Portal</p>
        </div>

        {/* Login form */}
        <div className="p-8">

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-spruce-500 focus:border-transparent transition"
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-spruce-500 focus:border-transparent transition"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg flex items-start gap-2">
                <span className="text-red-500 mt-0.5">!</span>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-spruce-600 text-white py-3 px-6 rounded-xl hover:bg-spruce-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Secure access to patient care management
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;